import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";

interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((o) => !o), []);

  // Cmd/Ctrl+K is owned by the sidebar quick-find (see global-quick-find.tsx).
  // The Command Palette uses Cmd/Ctrl+Shift+K so the two hotkeys don't fight.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;
      if (!isCmdOrCtrl) return;
      if (!e.shiftKey) return;
      if (e.key !== "k" && e.key !== "K") return;

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isContentEditable = target?.isContentEditable === true;
      const isFormField = tag === "input" || tag === "textarea" || tag === "select" || isContentEditable;
      if (isFormField) {
        const inputType = (target as HTMLInputElement | null)?.type?.toLowerCase();
        const isPlainText =
          tag === "textarea" ||
          isContentEditable ||
          (tag === "input" && (!inputType || ["text", "search", "email", "url", "tel", "password", "number"].includes(inputType)));
        if (isPlainText) return;
      }

      e.preventDefault();
      toggle();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  return (
    <CommandPaletteContext.Provider value={{ open, setOpen, toggle }}>
      {children}
    </CommandPaletteContext.Provider>
  );
}

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error("useCommandPalette must be used inside CommandPaletteProvider");
  }
  return ctx;
}
